import React, { useEffect, useState } from 'react'
import {
  Dialog, Box, Typography, IconButton, Tooltip, TextField, MenuItem,
  CircularProgress, useMediaQuery
} from '@mui/material'
import CloseIcon from '@mui/icons-material/CloseRounded'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphoneRounded'
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindowsOutlined'
import RefreshIcon from '@mui/icons-material/RefreshRounded'
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded'
import { useTranslation } from 'react-i18next'
import { embedUrl } from '../../utils/storeLinks'
import { mw, displaySx, iconBtnSx, dialogPaperSx } from '../../views/menuWorkspaceStyles'

// The phone most customers order on (iPhone 14/15), plus the bezel.
const PHONE = { width: 390, height: 844, bezel: 10 }

// The real customer page, not a copy of it, so the preview can never drift
// from what customers get. ?menu= opens one menu's tab: the web app reads a
// menu id, or "catering" for the Catering tab (rushmyfood-web utils/menuLinks.js).
const previewUrl = (slug, menu) => {
  const base = embedUrl(slug)
  if (!menu) return base
  return `${base}?menu=${menu.isCatering ? 'catering' : encodeURIComponent(menu._id)}`
}

const segBtnSx = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  height: 34,
  px: 1.5,
  border: 0,
  cursor: 'pointer',
  borderRadius: '9px',
  fontFamily: mw.font.body,
  fontWeight: 600,
  fontSize: 13.5,
  bgcolor: active ? '#fff' : 'transparent',
  color: active ? mw.color.textPrimary : mw.color.textMuted,
  boxShadow: active ? mw.shadow.pill : 'none',
  transition: 'background-color .15s ease, color .15s ease',
  '&:hover': { color: mw.color.textPrimary },
  '&:focus-visible': { outline: `2px solid ${mw.color.accent}`, outlineOffset: 2 },
  '& svg': { fontSize: 18 },
})

const MenuPreviewDialog = ({ open, onClose, slug, menus = [], initialMenuId = '' }) => {
  const { t } = useTranslation()
  // On a phone-sized screen the admin itself is the phone: go full screen.
  const narrow = useMediaQuery('(max-width:700px)')
  const [device, setDevice] = useState('desktop')
  const [menuId, setMenuId] = useState('')
  const [reloadKey, setReloadKey] = useState(0)
  const [loading, setLoading] = useState(true)

  // Each open starts on the menu that was clicked, or where customers land.
  useEffect(() => {
    if (open) setMenuId(initialMenuId ? String(initialMenuId) : '')
  }, [open, initialMenuId])

  const menu = menus.find(m => String(m._id) === menuId) || null
  const url = slug ? previewUrl(slug, menu) : ''
  const asPhone = narrow || device === 'phone'

  // A new address or a reload remounts the frame (a hash-only change would not
  // reload the page), so show the spinner until it finishes loading.
  useEffect(() => { setLoading(true) }, [url, reloadKey])

  const frameSx = narrow
    ? { width: '100%', height: '100%' }
    : asPhone
      ? {
        width: PHONE.width + 2 * PHONE.bezel,
        height: '100%',
        maxHeight: PHONE.height + 2 * PHONE.bezel,
        alignSelf: 'center',
        flexShrink: 0,
        border: `${PHONE.bezel}px solid ${mw.color.textPrimary}`,
        borderRadius: '44px',
        boxShadow: mw.shadow.pop,
      }
      : {
        width: '100%',
        height: '100%',
        border: `1px solid ${mw.color.border}`,
        borderRadius: mw.radius.panel,
        boxShadow: mw.shadow.card,
      }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={narrow}
      maxWidth={false}
      PaperProps={{
        sx: narrow
          ? { bgcolor: mw.color.pageBg, borderRadius: 0 }
          : {
            ...dialogPaperSx,
            width: 'min(1320px, calc(100% - 48px))',
            height: 'calc(100% - 48px)',
            maxHeight: 'none',
            m: 3,
            overflow: 'hidden',
            bgcolor: mw.color.pageBg,
          },
      }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: { xs: 2, sm: 3 }, py: 1.75, bgcolor: mw.color.cardBg, borderBottom: `1px solid ${mw.color.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 'auto', minWidth: 0 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: mw.radius.sm, bgcolor: mw.color.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <VisibilityIcon sx={{ color: mw.color.accent, fontSize: 21 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={displaySx(20)}>{t('Preview')}</Typography>
            <Typography sx={{ fontSize: 13, color: mw.color.textMuted, mt: 0.25 }}>
              {t('Your menu as customers see it on the RushMyFood website')}
            </Typography>
          </Box>
        </Box>

        {menus.length > 0 && (
          <TextField
            select
            size="small"
            value={menuId}
            onChange={e => setMenuId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ 'aria-label': t('Menu to preview') }}
            sx={{ minWidth: 210, '& .MuiOutlinedInput-root': { borderRadius: mw.radius.xs, bgcolor: mw.color.cardBg } }}>
            <MenuItem value="">{t('Where customers land')}</MenuItem>
            {menus.map(m => (
              <MenuItem key={m._id} value={String(m._id)}>{m.name}</MenuItem>
            ))}
          </TextField>
        )}

        {!narrow && (
          <Box role="group" aria-label={t('Screen size')} sx={{ display: 'inline-flex', gap: '4px', p: '4px', bgcolor: mw.color.segmentBg, borderRadius: '12px' }}>
            <Box component="button" type="button" aria-pressed={device === 'phone'} onClick={() => setDevice('phone')} sx={segBtnSx(device === 'phone')}>
              <PhoneIphoneIcon />{t('Phone')}
            </Box>
            <Box component="button" type="button" aria-pressed={device === 'desktop'} onClick={() => setDevice('desktop')} sx={segBtnSx(device === 'desktop')}>
              <DesktopWindowsIcon />{t('Desktop')}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('Reload')}>
            <IconButton onClick={() => setReloadKey(k => k + 1)} sx={iconBtnSx} aria-label={t('Reload')}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Open in a new tab')}>
            <IconButton component="a" href={url} target="_blank" rel="noopener noreferrer" sx={iconBtnSx} aria-label={t('Open in a new tab')}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Close')}>
            <IconButton onClick={onClose} sx={iconBtnSx} aria-label={t('Close')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {menu && !menu.isActive && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, bgcolor: mw.color.amberSoft, borderBottom: `1px solid ${mw.color.amberBorder}` }}>
          <Typography sx={{ fontSize: 13, color: mw.color.amber, fontWeight: 500 }}>
            {t('This menu is turned off, so customers do not see it. The preview shows your other menus.')}
          </Typography>
        </Box>
      )}

      {/* Stage */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', p: narrow ? 0 : 3 }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#fff', ...frameSx }}>
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: '#fff', zIndex: 1 }}>
              <CircularProgress size={28} sx={{ color: mw.color.accent }} />
              <Typography sx={{ fontSize: 13.5, color: mw.color.textMuted }}>{t('Loading your store...')}</Typography>
            </Box>
          )}
          {url ? (
            <iframe
              key={`${url}|${reloadKey}`}
              title={t('Menu preview')}
              src={url}
              onLoad={() => setLoading(false)}
              allow="geolocation *; payment *"
              style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
            />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, zIndex: 2, bgcolor: '#fff' }}>
              <Typography sx={{ color: mw.color.textMuted, textAlign: 'center' }}>
                {t('This restaurant has no web address yet, so there is nothing to preview.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}

export default MenuPreviewDialog
